import { Controller } from "@hotwired/stimulus"

// Presents scale calibration correction in operational language while keeping
// the stored value in the canonical signed-additive convention:
//   reads high => negative correction; reads low => positive correction.
export default class extends Controller {
  static targets = ["display", "tare", "direction", "magnitude", "signed", "net", "formula"]

  connect() {
    const signed = this.number(this.signedTarget.value)
    if (signed < 0) {
      this.directionTarget.value = "reads_high"
      this.magnitudeTarget.value = Math.abs(signed)
    } else if (signed > 0) {
      this.directionTarget.value = "reads_low"
      this.magnitudeTarget.value = Math.abs(signed)
    }
    this.sync()
  }

  sync() {
    const direction = this.directionTarget.value
    const magnitude = Math.abs(this.number(this.magnitudeTarget.value))
    const correction = direction === "reads_high" ? -magnitude : (direction === "reads_low" ? magnitude : 0)

    this.magnitudeTarget.disabled = direction === "none"
    this.magnitudeTarget.required = direction !== "none"
    this.signedTarget.value = direction === "none" || this.magnitudeTarget.value === "" ? "" : correction
    const missingDifference = direction !== "none" && this.magnitudeTarget.value === ""
    this.magnitudeTarget.classList.toggle("is-invalid", missingDifference)
    this.magnitudeTarget.setAttribute("aria-invalid", missingDifference.toString())

    const displayPresent = this.displayTarget.value !== ""
    if (!displayPresent) {
      this.netTarget.textContent = "— kg"
      this.netTarget.classList.remove("text-danger", "text-success")
      this.formulaTarget.textContent = "Enter the scale display"
      return
    }

    if (missingDifference) {
      this.netTarget.textContent = "— kg"
      this.netTarget.classList.remove("text-danger", "text-success")
      this.formulaTarget.textContent = "Enter the correction difference"
      return
    }

    const display = this.number(this.displayTarget.value)
    const tare = this.number(this.tareTarget.value)
    const net = display - tare + correction
    this.netTarget.textContent = `${this.format(net)} kg`
    this.netTarget.classList.toggle("text-danger", net < 0)
    this.netTarget.classList.toggle("text-success", net >= 0)

    const correctionText = correction === 0 ? "" : ` ${correction < 0 ? "−" : "+"} ${this.format(Math.abs(correction))}`
    this.formulaTarget.textContent = `${this.format(display)} − ${this.format(tare)}${correctionText}`
  }

  number(value) {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  format(value) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  }
}
